/**
 * Branch Drift — dedicated page for the main ↔ staging drift snapshot.
 *
 * Item 1 of "Stop Sunday from happening again". Surfaces the same data the
 * dashboard widget shows, plus the catch-up commands and per-commit detail.
 *
 * Endpoint: GET /api/admin/cicd/branch-drift (60s server-side cache).
 * Polls every 30s; user can hit "Refresh" to bust the local stale window
 * (server still returns the cached value if < 60s old).
 */
import React, { useEffect, useState } from 'react';
import { GitBranch, RefreshCw, ExternalLink, Copy, Check, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { adminFetch } from '../../../utils/adminApi';

// ── Types (mirror routers/admin_branch_drift.py response) ────────────────

interface BranchHead {
  sha: string;
  msg: string;
  author: string;
  when: string;
}

interface DriftCommit {
  sha: string;
  msg: string;
  author: string;
  age_minutes: number;
}

interface DriftResponse {
  main: BranchHead | null;
  staging: BranchHead | null;
  drift: { staging_ahead: number; main_ahead: number };
  staging_only_commits: DriftCommit[];
  main_only_commits: DriftCommit[];
  alert_level: 'ok' | 'warn' | 'critical';
  alert_reasons: string[];
  fetched_at: string;
  cached: boolean;
  error: string | null;
}

// ── Helpers ──────────────────────────────────────────────────────────────

function formatAge(min: number): string {
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m`;
  const hours = Math.floor(min / 60);
  const rem = min % 60;
  if (hours < 24) return rem > 0 ? `${hours}h ${rem}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
}

function colorForLevel(level: string): { bg: string; fg: string; border: string } {
  if (level === 'critical') return { bg: 'var(--status-error-bg)', fg: 'var(--status-error-text)', border: 'var(--status-error-bg)' };
  if (level === 'warn') return { bg: 'var(--status-warning-bg)', fg: 'var(--status-warning-text)', border: 'var(--status-warning-bg)' };
  return { bg: 'var(--status-success-bg)', fg: 'var(--status-success-text)', border: 'var(--status-success-bg)' };
}

const REPO_HTML_BASE = 'https://github.com/Coach-Cedar/research-beta/commit/';

// ── Copyable code block ─────────────────────────────────────────────────

const CodeBlock: React.FC<{ code: string; label?: string }> = ({ code, label }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid var(--text-primary)' }}>
      {label && (
        <div style={{ background: 'var(--text-primary)', padding: '6px 12px', fontSize: 10, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          {label}
        </div>
      )}
      <div style={{ background: 'var(--text-primary)', padding: '12px 16px', position: 'relative' }}>
        <button
          onClick={handleCopy}
          style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, color: 'var(--cedar-subtle)', fontSize: 11 }}
        >
          {copied ? <Check style={{ width: 12, height: 12 }} /> : <Copy style={{ width: 12, height: 12 }} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
        <pre style={{ fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace", fontSize: 12, color: 'var(--cedar-mist)', margin: 0, whiteSpace: 'pre-wrap', lineHeight: 1.5, paddingRight: 60 }}>{code}</pre>
      </div>
    </div>
  );
};

// ── Commit row ──────────────────────────────────────────────────────────

const CommitRow: React.FC<{ c: DriftCommit }> = ({ c }) => {
  const stale = c.age_minutes >= 60;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderBottom: '1px solid var(--surface-muted)' }}>
      <code style={{ fontSize: 12, fontFamily: 'monospace', color: 'var(--text-strong)', minWidth: 60 }}>{c.sha}</code>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.msg}</div>
        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{c.author}</div>
      </div>
      <div style={{ fontSize: 12, color: stale ? 'var(--status-error-text)' : 'var(--text-secondary)', fontWeight: stale ? 600 : 400 }}>
        {formatAge(c.age_minutes)}
      </div>
      <a href={`${REPO_HTML_BASE}${c.sha}`} target="_blank" rel="noreferrer" style={{ color: 'var(--cedar-stream)', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
        view <ExternalLink style={{ width: 12, height: 12 }} />
      </a>
    </div>
  );
};

// ── Page ────────────────────────────────────────────────────────────────

export default function BranchDrift() {
  const [data, setData] = useState<DriftResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [refreshedAt, setRefreshedAt] = useState<number>(Date.now());

  const fetchDrift = async () => {
    try {
      const res = await adminFetch<DriftResponse>('/api/admin/cicd/branch-drift');
      setData(res);
      setErr(null);
      setRefreshedAt(Date.now());
    } catch (e: any) {
      setErr(e?.message ?? 'Failed to fetch drift data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrift();
    const id = setInterval(fetchDrift, 30_000);
    return () => clearInterval(id);
  }, []);

  const color = colorForLevel(data?.alert_level ?? 'ok');
  const Icon = data?.alert_level === 'ok' ? CheckCircle2 : AlertTriangle;

  const mainAheadCmd = 'git push origin origin/main:staging';
  const stagingAheadCmd = "# Open a PR: gh pr create --base main --head staging --title 'release: staging → main'";

  const secondsAgo = Math.max(0, Math.floor((Date.now() - refreshedAt) / 1000));

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <GitBranch style={{ width: 24, height: 24, color: 'var(--text-primary)' }} />
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Branch Drift — main ↔ staging</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            Last refreshed: {secondsAgo}s ago{data?.cached ? ' (cached)' : ''}
          </span>
          <button
            onClick={fetchDrift}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'var(--surface)', cursor: 'pointer', fontSize: 13 }}
          >
            <RefreshCw style={{ width: 14, height: 14 }} /> Refresh
          </button>
        </div>
      </div>

      {/* Status banner */}
      <div style={{ background: color.bg, border: `1px solid ${color.border}`, borderRadius: 12, padding: 20, marginBottom: 24, display: 'flex', alignItems: 'flex-start', gap: 16 }}>
        <Icon style={{ width: 28, height: 28, color: color.fg, flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: color.fg, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {data?.alert_level ?? '—'}
          </div>
          {loading && !data ? (
            <div style={{ fontSize: 14, color: color.fg, marginTop: 4 }}>Loading…</div>
          ) : err ? (
            <div style={{ fontSize: 14, color: color.fg, marginTop: 4 }}>Error: {err}</div>
          ) : data ? (
            <>
              <div style={{ fontSize: 14, color: color.fg, marginTop: 4 }}>
                staging ahead by <strong>{data.drift.staging_ahead}</strong> commit(s) ·
                main ahead by <strong>{data.drift.main_ahead}</strong> commit(s)
              </div>
              {data.alert_reasons.length > 0 && (
                <ul style={{ margin: '8px 0 0 18px', padding: 0, fontSize: 13, color: color.fg }}>
                  {data.alert_reasons.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              )}
            </>
          ) : null}
        </div>
      </div>

      {/* Side-by-side panels */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
        <div style={{ background: 'var(--surface)', borderRadius: 12, boxShadow: '0px 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>main ahead by {data?.drift.main_ahead ?? 0}</div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>not yet on staging</div>
          </div>
          {data && data.main_only_commits.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>No commits stuck on main.</div>
          ) : (
            data?.main_only_commits.map(c => <CommitRow key={c.sha} c={c} />)
          )}
        </div>

        <div style={{ background: 'var(--surface)', borderRadius: 12, boxShadow: '0px 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>staging ahead by {data?.drift.staging_ahead ?? 0}</div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>not yet on main</div>
          </div>
          {data && data.staging_only_commits.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>No commits stuck on staging.</div>
          ) : (
            data?.staging_only_commits.map(c => <CommitRow key={c.sha} c={c} />)
          )}
        </div>
      </div>

      {/* Catch-up commands */}
      <div style={{ background: 'var(--surface)', borderRadius: 12, boxShadow: '0px 1px 3px rgba(0,0,0,0.1)', padding: 20, marginBottom: 24 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12 }}>Catch-up commands</div>
        {data && data.drift.main_ahead > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>Fast-forward staging to match main:</div>
            <CodeBlock code={mainAheadCmd} label="main → staging" />
          </div>
        )}
        {data && data.drift.staging_ahead > 0 && (
          <div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>Open the staging → main release PR:</div>
            <CodeBlock code={stagingAheadCmd} label="staging → main" />
          </div>
        )}
        {data && data.drift.main_ahead === 0 && data.drift.staging_ahead === 0 && (
          <div style={{ fontSize: 13, color: 'var(--status-success-text)' }}>Branches are in sync. Nothing to do.</div>
        )}
      </div>

      {/* History placeholder */}
      <div style={{ background: 'var(--surface-subtle)', borderRadius: 12, padding: 16, fontSize: 12, color: 'var(--text-secondary)', textAlign: 'center' }}>
        Previous 7 days of resolved drift incidents — history coming soon.
      </div>
    </div>
  );
}
